class BuildYourBundle {
  constructor() {
    this.productId = document.querySelector('.build-your-bundle').dataset.productId;
    this.boxData = null;
    this.selectedPlan = null;
    this.cart = new Map();
    this.boxIndex = 0;
    this.showingUpsells = false;
    this.upsellProducts = [];
    
    this.init();
  }

  async init() {
    try {
      await Promise.all([
        this.fetchBoxData(),
        this.fetchUpsellProducts()
      ]);
      this.hideLoading();
      this.renderUI();
      this.bindEvents();
      this.setDefaultPlan();
      
      // Set initial button text
      const addToCartBtn = document.getElementById('add-to-cart');
      const buttonDiv = addToCartBtn.querySelector('div');
      if (buttonDiv) {
        buttonDiv.textContent = 'Next';
      } else {
        addToCartBtn.textContent = 'Next';
      }
      
      // Setup mobile cart toggle
      this.setupMobileCartToggle();
    } catch (error) {
      console.error('Failed to initialize bundle:', error);
      this.showError('Failed to load bundle options. Please refresh the page.');
    }
  }

  async fetchUpsellProducts() {
    try {
      const response = await fetch('/collections/skio-build-your-own-bundle-upsell-items/products.json');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.upsellProducts = data.products.map(product => ({
        id: product.id.toString(),
        handle: product.handle,
        title: product.title,
        featured_image: product.images[0]?.src || '',
        price: product.variants[0]?.price || 0,
        variantId: product.variants[0]?.id || 0
      }));
      console.log('Fetched upsell products:', this.upsellProducts);
    } catch (error) {
      console.error('Failed to fetch upsell products:', error);
      this.upsellProducts = [];
    }
  }

  async fetchBoxData() {
    const response = await fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': '*/*'
      },
      body: JSON.stringify({
        productPlatformId: this.productId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data.ok || !data.DynamicBox) {
      throw new Error('Invalid response from Skio API');
    }

    this.boxData = data.DynamicBox;
    console.log('Box data loaded:', this.boxData);
  }

  hideLoading() {
    document.getElementById('loading-state').style.display = 'none';
    document.getElementById('bundle-container').style.display = 'block';
  }

  setupMobileCartToggle() {
    const toggleBtn = document.getElementById('mobile-cart-toggle');
    const cartItems = document.getElementById('cart-items');
    const toggleIcon = document.getElementById('toggle-icon');
    const toggleText = document.getElementById('toggle-text');
    
    if (toggleBtn && cartItems && toggleIcon && toggleText) {
      toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const isShowing = cartItems.classList.contains('show');
        console.log('Toggle clicked, currently showing:', isShowing);
        
        if (isShowing) {
          // Hide items
          cartItems.classList.remove('show');
          toggleText.textContent = 'Show items';
          toggleIcon.innerHTML = '<path d="m7 14 5-5 5 5z"></path>'; // Arrow up
          console.log('Items hidden');
        } else {
          // Show items
          cartItems.classList.add('show');
          toggleText.textContent = 'Hide items';
          toggleIcon.innerHTML = '<path d="m7 10 5 5 5-5z"></path>'; // Arrow down
          console.log('Items shown');
        }
      });
      console.log('Mobile cart toggle initialized');
    } else {
      console.log('Mobile cart toggle elements not found:', {
        toggleBtn: !!toggleBtn,
        cartItems: !!cartItems,
        toggleIcon: !!toggleIcon,
        toggleText: !!toggleText
      });
    }
  }

  renderUI() {
    this.renderHeader();
    this.renderSubscriptionOptions();
    this.renderProgressBar();
    this.renderProductGroups();
    this.applyTheme();
  }

  renderHeader() {
    document.getElementById('bundle-title').textContent = this.boxData.settings.header || 'Build Your Bundle';
    document.getElementById('bundle-subtitle').textContent = this.boxData.settings.subheader || '';
  }

  renderSubscriptionOptions() {
    const container = document.getElementById('subscription-options');
    
    // Add one-time option if not hidden
    if (!this.boxData.settings.ignoreOneTime) {
      const oneTimeBtn = this.createSubscriptionButton('onetime', 'One time');
      container.appendChild(oneTimeBtn);
    }

    // Add subscription plans
    Object.entries(this.boxData.sellingPlanGroup || {}).forEach(([planId, plan]) => {
      const btn = this.createSubscriptionButton(planId, plan.title, plan.priceAdjustmentAmount);
      container.appendChild(btn);
    });
  }

  createSubscriptionButton(planId, title, discount = null) {
    const button = document.createElement('button');
    button.className = 'subscription-btn';
    button.dataset.plan = planId;
    
    const titleSpan = document.createElement('span');
    titleSpan.className = 'plan-title';
    titleSpan.textContent = title;
    button.appendChild(titleSpan);

    if (discount && discount > 0) {
      const badge = document.createElement('div');
      badge.className = 'discount-badge';
      badge.textContent = `${discount}% off`;
      button.appendChild(badge);
    }

    return button;
  }

  renderProgressBar() {
    const container = document.getElementById('progress-markers');
    const tiers = this.boxData.percentDiscountTier || {};
    
    // Calculate max value for positioning
    const sortedTiers = Object.entries(tiers).sort(([a], [b]) => parseFloat(a) - parseFloat(b));
    const maxThreshold = sortedTiers.length > 0 ? parseFloat(sortedTiers[sortedTiers.length - 1][0]) : 100;
    
    // Add starting point at $0
    const startMarker = document.createElement('div');
    startMarker.className = 'progress-marker';
    startMarker.style.left = '0%';
    startMarker.innerHTML = `
      <div class="marker-amount">$0</div>
      <div class="marker-spacer"></div>
      <div class="marker-label">Min</div>
    `;
    container.appendChild(startMarker);
    
    // Create markers for discount tiers
    sortedTiers.forEach(([threshold, discount], index) => {
      const marker = document.createElement('div');
      marker.className = 'progress-marker';
      
      // Calculate position as percentage
      const position = (parseFloat(threshold) / maxThreshold) * 100;
      const isLast = index === sortedTiers.length - 1;
      
      // Add special class for last marker
      if (isLast) {
        marker.classList.add('last-marker');
      }
      
      marker.style.left = `${Math.min(position, 100)}%`;
      
      marker.innerHTML = `
        <div class="marker-amount">$${threshold}</div>
        <div class="marker-spacer"></div>
        <div class="marker-label">${discount}% off</div>
      `;
      container.appendChild(marker);
    });
  }

  renderProductGroups() {
    const container = document.getElementById('product-groups');
    const products = this.boxData.selectableProductVariants || [];
    
    // Group products by category/type
    const groupedProducts = this.groupProductsByCategory(products);
    
    // Render each group
    Object.entries(groupedProducts).forEach(([categoryName, categoryProducts]) => {
      const groupDiv = document.createElement('div');
      groupDiv.className = 'product-group';
      
      const groupTitle = document.createElement('h2');
      groupTitle.textContent = categoryName;
      groupTitle.style.color = this.boxData.theme?.groupHeader || '#000';
      groupTitle.style.fontSize = '24px';
      groupTitle.style.fontWeight = '600';
      groupTitle.style.marginBottom = '48px';
      groupDiv.appendChild(groupTitle);

      const productsGrid = document.createElement('div');
      productsGrid.className = 'products-grid';

      categoryProducts.forEach(product => {
        const card = this.createProductCard(product);
        productsGrid.appendChild(card);
      });

      groupDiv.appendChild(productsGrid);
      container.appendChild(groupDiv);
    });
  }

  groupProductsByCategory(products) {
    const groups = {};
    
    products.forEach(product => {
      // Extract category from product title or use product type
      let category = this.extractCategoryFromProduct(product);
      
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(product);
    });
    
    return groups;
  }

  extractCategoryFromProduct(product) {
    const title = product.productTitle.toLowerCase();
    const handle = product.productHandle ? product.productHandle.toLowerCase() : '';
    
    // Define category keywords and their display names (order matters - more specific first)
    const categoryMap = {
      'mouth tape': 'Toothbrushes & Mouth Tape',
      'toothpaste': 'Toothpaste',
      'floss': 'Floss',
      'probiotic': 'Probiotics',
      'mouthwash': 'Mouthwash',
      'mouth wash': 'Mouthwash',
      'toothbrush': 'Toothbrushes & Mouth Tape',
      'bamboo': 'Toothbrushes & Mouth Tape',
      'charcoal': 'Toothpaste', // Charcoal toothpaste
      'whitening': 'Toothpaste', // Whitening toothpaste
      'strawberry': 'Toothpaste', // Strawberry toothpaste
      'cinnamint': 'Toothpaste', // Cinnamint toothpaste
      'mint': 'Probiotics', // Mint probiotics (check after toothpaste)
      'raspberry': 'Probiotics', // Raspberry probiotics
      'lemonade': 'Probiotics', // Raspberry lemonade probiotics
      'silk': 'Floss', // Silk floss
      'picks': 'Floss', // Floss picks
      'balanced': 'Mouthwash', // Balanced mouth blend
      'blend': 'Mouthwash',
      'feather': 'Toothbrushes & Mouth Tape', // Feather bristle toothbrush
      'bristle': 'Toothbrushes & Mouth Tape',
      'micro': 'Toothbrushes & Mouth Tape', // Micro bristle toothbrush
      'children': 'Toothbrushes & Mouth Tape' // Children's toothbrushes
    };
    
    // Check for category keywords in product title first
    for (const [keyword, category] of Object.entries(categoryMap)) {
      if (title.includes(keyword)) {
        return category;
      }
    }
    
    // If no category found in title, try product handle
    if (handle) {
      for (const [keyword, category] of Object.entries(categoryMap)) {
        if (handle.includes(keyword)) {
          return category;
        }
      }
    }
    
    // Default category if none found
    return 'Other Products';
  }

  createProductCard(product) {
    const card = document.createElement('div');
    card.className = 'product-card';
    card.dataset.productId = product.productPlatformId;

    const variant = product.productVariants[0];
    const originalPrice = variant.price;

    card.innerHTML = `
      <div class="product-image-container">
        <img src="${product.productImageSrc}" alt="${product.productTitle}" loading="lazy">
        <button class="info-btn" aria-label="Learn more about ${product.productTitle}">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#filter0_b_417_42373)">
              <rect width="32" height="32" rx="6" fill="white" fill-opacity="0.8"></rect>
              <path d="M11 21L21 11M21 11H14.3333M21 11V17.6667" stroke="#373F51" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
            </g>
            <defs>
              <filter id="filter0_b_417_42373" x="-16" y="-16" width="64" height="64" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
                <feGaussianBlur in="BackgroundImageFix" stdDeviation="8"></feGaussianBlur>
                <feComposite in2="SourceAlpha" operator="in" result="effect1_backgroundBlur_417_42373"></feComposite>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_backgroundBlur_417_42373" result="shape"></feBlend>
              </filter>
            </defs>
          </svg>
        </button>
      </div>
      <div class="product-title-container">
        <div class="product-title">${product.productTitle}</div>
      </div>
      <div class="product-price-container">
        <div class="original-price"><s>${this.formatPrice(originalPrice)}</s></div>
        <div class="discounted-price">${this.formatPrice(originalPrice)}</div>
      </div>
      <div class="quantity-selector">
        <button class="qty-btn minus" data-action="decrease" aria-label="Remove one ${product.productTitle}. Current quantity: 0">
          <div class="btn-icon">
            <svg height="12" width="12" viewBox="0 0 24 24" aria-hidden="true" role="img">
              <path d="M24 12a2 2 0 01-2 2H2a2 2 0 110-4h20a2 2 0 012 2z"></path>
            </svg>
          </div>
        </button>
        <input type="number" class="qty-input" value="0" min="0" aria-label="Quantity of ${product.productTitle}">
        <button class="qty-btn plus" data-action="increase" aria-label="Add one ${product.productTitle}. Current quantity: 0">
          <div class="btn-icon">
            <svg height="12" width="12" viewBox="0 0 24 24" aria-hidden="true" role="img">
              <path d="M22 10h-8V2a2 2 0 0 0-4 0v8H2a2 2 0 0 0 0 4h8v8a2 2 0 0 0 4 0v-8h8a2 2 0 0 0 0-4"></path>
            </svg>
          </div>
        </button>
      </div>
    `;

    return card;
  }

  bindEvents() {
    // Subscription toggle
    document.getElementById('subscription-options').addEventListener('click', (e) => {
      const btn = e.target.closest('.subscription-btn');
      if (btn) {
        this.handlePlanChange(btn.dataset.plan);
      }
    });

    // Quantity controls and info buttons
    document.getElementById('product-groups').addEventListener('click', (e) => {
      const qtyBtn = e.target.closest('.qty-btn');
      if (qtyBtn) {
        this.handleQuantityChange(qtyBtn);
        return;
      }

      // Info button clicks
      const infoBtn = e.target.closest('.info-btn');
      if (infoBtn) {
        this.handleInfoButtonClick(infoBtn);
        return;
      }
    });

    // Next/Back button
    document.getElementById('add-to-cart').addEventListener('click', async () => {
      if (this.showingUpsells) {
        // Add selected bundle items to cart first
        await this.handleAddToCart();
      } else {
        // If showing main products, show upsells
        this.handleShowUpsells();
      }
    });
  }

  setDefaultPlan() {
    // Set first available plan as default
    const firstBtn = document.querySelector('.subscription-btn');
    if (firstBtn) {
      this.handlePlanChange(firstBtn.dataset.plan);
    }
  }

  handlePlanChange(planId) {
    // Update active state
    document.querySelectorAll('.subscription-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    document.querySelector(`[data-plan="${planId}"]`).classList.add('active');
    
    this.selectedPlan = planId;
    this.updatePricing();
    this.updateCartUI(); // Update cart sidebar with new prices and discounts
  }

  handleQuantityChange(button) {
    const card = button.closest('.product-card');
    const input = card.querySelector('.qty-input');
    const productId = card.dataset.productId;
    const action = button.dataset.action;
    
    let currentQty = parseInt(input.value) || 0;
    let newQty = currentQty;

    if (action === 'increase') {
      if (this.canIncreaseQuantity()) {
        newQty = currentQty + 1;
      }
    } else if (action === 'decrease') {
      newQty = Math.max(0, currentQty - 1);
    }

    if (newQty !== currentQty) {
      input.value = newQty;
      this.updateCart(productId, newQty);
    }
  }

  canIncreaseQuantity() {
    const totalItems = this.getTotalItems();
    const maxItems = this.boxData.sizeRange?.max;
    
    if (maxItems && totalItems >= maxItems) {
      this.showError(`Maximum ${maxItems} items allowed`);
      return false;
    }
    
    return true;
  }

  updateCart(productId, quantity) {
    if (quantity > 0) {
      this.cart.set(productId, quantity);
    } else {
      this.cart.delete(productId);
    }
    
    this.updateCartUI();
    this.updatePricing();
  }

  updateCartUI() {
    const totalItems = this.getTotalItems();
    const subtotal = this.calculateSubtotal();
    const discount = this.calculateDiscount(subtotal);
    
    // Update cart items display
    const cartItemsContainer = document.getElementById('cart-items');
    cartItemsContainer.innerHTML = '';
    
    // Separate bundle and upsell items
    const bundleItems = [];
    const upsellItems = [];

    this.cart.forEach((quantity, productId) => {
      // First try to find in regular products
      let product = this.boxData.selectableProductVariants.find(p => p.productPlatformId === productId);
      let isUpsell = false;

      // If not found, check upsell products
      if (!product) {
        const upsellProduct = this.upsellProducts.find(p => p.id === productId);
        if (upsellProduct) {
          isUpsell = true;
          product = {
            productTitle: upsellProduct.title,
            productImageSrc: upsellProduct.featured_image,
            productVariants: [{
              price: upsellProduct.price
            }]
          };
        }
      }

      if (product) {
        const originalPrice = product.productVariants[0].price;
        const discountedPrice = isUpsell ? originalPrice : this.getDiscountedPrice(originalPrice, productId);
        
        const cartItem = document.createElement('div');
        cartItem.className = 'cart-item';
        cartItem.dataset.productId = productId;
        
        cartItem.innerHTML = `
          <div class="cart-item-image-container">
            <img src="${product.productImageSrc}" alt="${product.productTitle}" class="cart-item-image">
            <div class="cart-item-count">
              <div tabindex="0" style="color: white; font-size: 12px; font-weight: 400;">${quantity}</div>
            </div>
            <button class="cart-item-remove" aria-label="Remove ${product.productTitle}">
              <div style="width: 10px; height: 10px;">
                <svg class="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="ClearIcon">
                  <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
                </svg>
              </div>
            </button>
          </div>
          <div class="cart-item-title">
            <div tabindex="0">${product.productTitle}</div>
          </div>
          <div class="cart-item-price">
            ${this.selectedPlan === 'onetime' || originalPrice === discountedPrice ? `
              <div tabindex="0" class="cart-item-price-discounted">${this.formatPrice(originalPrice)}</div>
            ` : `
              <div tabindex="0" class="cart-item-price-original"><s>${this.formatPrice(originalPrice)}</s></div>
              <div tabindex="0" class="cart-item-price-discounted">${this.formatPrice(discountedPrice)}</div>
            `}
          </div>
        `;
        
        // Add click handler for remove button
        const removeBtn = cartItem.querySelector('.cart-item-remove');
        removeBtn.addEventListener('click', () => {
          this.updateCart(productId, 0);
        });
        
        if (isUpsell) {
          upsellItems.push(cartItem);
        } else {
          bundleItems.push(cartItem);
        }
      }
    });

    // For mobile, create separate sections for bundle and upsell items
    if (window.innerWidth <= 768) {
      // Bundle items section
      if (bundleItems.length > 0) {
        const bundleContainer = document.createElement('div');
        bundleContainer.className = 'bundle-items-container';
        
        const bundleHeader = document.createElement('div');
        bundleHeader.className = 'section-header';
        bundleHeader.textContent = 'Your box';
        bundleContainer.appendChild(bundleHeader);
        
        const bundleRow = document.createElement('div');
        bundleRow.className = 'items-row';
        bundleItems.forEach(item => bundleRow.appendChild(item));
        bundleContainer.appendChild(bundleRow);
        
        cartItemsContainer.appendChild(bundleContainer);
      }
      
      // Upsell items section
      if (upsellItems.length > 0) {
        const upsellContainer = document.createElement('div');
        upsellContainer.className = 'upsell-items-container';
        
        // Only show upsell header if both bundle and upsell items exist
        if (bundleItems.length > 0) {
          const upsellHeader = document.createElement('div');
          upsellHeader.className = 'section-header';
          upsellHeader.textContent = 'One time add-ons';
          upsellContainer.appendChild(upsellHeader);
        }
        
        const upsellRow = document.createElement('div');
        upsellRow.className = 'items-row';
        upsellItems.forEach(item => upsellRow.appendChild(item));
        upsellContainer.appendChild(upsellRow);
        
        cartItemsContainer.appendChild(upsellContainer);
      }
    } else {
      // Desktop layout
      bundleItems.forEach(item => cartItemsContainer.appendChild(item));
      
      if (upsellItems.length > 0) {
        const upsellHeading = document.createElement('div');
        upsellHeading.className = 'cart-section-heading';
        upsellHeading.style.cssText = `
          color: rgb(0, 0, 0);
          font-size: 14px;
          font-weight: 700;
        `;
        upsellHeading.textContent = 'One time add-ons';
        cartItemsContainer.appendChild(upsellHeading);
        
        upsellItems.forEach(item => cartItemsContainer.appendChild(item));
      }
    }
      
      // Show/hide items and discount rows based on cart state
      const itemsRow = document.querySelector('.items-row');
      const discountRow = document.querySelector('.discount-row');
      const subtotalElement = document.getElementById('subtotal');
      
      // Calculate final price after discount
      const finalPrice = subtotal - discount;
      
      if (totalItems > 0) {
        itemsRow.style.display = 'flex';
        document.getElementById('total-items').textContent = totalItems;
        
        // Only show discount if there is one
        if (discount > 0) {
          discountRow.style.display = 'flex';
          document.getElementById('total-discount').textContent = this.formatPrice(discount);
        } else {
          discountRow.style.display = 'none';
        }
        
        // Set subtotal to red when items in cart
        subtotalElement.style.color = 'rgb(234, 111, 97)';
      } else {
        itemsRow.style.display = 'none';
        discountRow.style.display = 'none';
        
        // Set subtotal to black when cart empty
        subtotalElement.style.color = '#000';
      }
      
      // Show subtotal after discount
      subtotalElement.textContent = this.formatPrice(finalPrice);
    
    // Enable/disable add to cart and set correct text
    const addToCartBtn = document.getElementById('add-to-cart');
    addToCartBtn.disabled = totalItems === 0;
    const buttonText = this.showingUpsells ? 'Checkout' : 'Next';
    const buttonDiv = addToCartBtn.querySelector('div');
    if (buttonDiv) {
      buttonDiv.textContent = buttonText;
    } else {
      addToCartBtn.textContent = buttonText;
    }
    
    // Update mobile cart elements
    const mobileItemsCount = document.getElementById('mobile-items-count');
    if (mobileItemsCount) {
      mobileItemsCount.textContent = totalItems;
    }
    
    const mobileOriginalTotal = document.getElementById('mobile-original-total');
    const mobileFinalTotal = document.getElementById('mobile-final-total');
    
    if (mobileOriginalTotal && mobileFinalTotal) {
      if (discount > 0) {
        mobileOriginalTotal.innerHTML = `<s>${this.formatPrice(subtotal)}</s>`;
        mobileOriginalTotal.style.display = 'block';
        mobileFinalTotal.textContent = this.formatPrice(finalPrice);
        mobileFinalTotal.style.color = 'rgb(234, 111, 97)';
      } else {
        mobileOriginalTotal.style.display = 'none';
        mobileFinalTotal.textContent = this.formatPrice(finalPrice);
        mobileFinalTotal.style.color = totalItems > 0 ? 'rgb(234, 111, 97)' : 'rgb(0, 0, 0)';
      }
    }
    
    // Show/hide cart sidebar on mobile based on items
    const cartSidebar = document.getElementById('cart-sidebar');
    if (cartSidebar) {
      if (window.innerWidth <= 768) {
        if (totalItems > 0) {
          cartSidebar.style.display = 'flex';
          console.log('Mobile cart shown - items:', totalItems);
        } else {
          cartSidebar.style.display = 'none';
          console.log('Mobile cart hidden - no items');
        }
      } else {
        // Desktop - always show
        cartSidebar.style.display = 'flex';
      }
    }
    
    // Update progress bar with final price (after discount for subscriptions)
    this.updateProgressBar(finalPrice);
  }

  updateProgressBar(subtotal) {
    const tiers = this.boxData.percentDiscountTier || {};
    const maxTier = Math.max(...Object.keys(tiers).map(t => parseFloat(t)));
    const progress = Math.min((subtotal / maxTier) * 100, 100);
    
    document.getElementById('progress-fill').style.width = `${progress}%`;
  }

  updatePricing() {
    // Update individual product prices based on selected plan
    document.querySelectorAll('.product-card').forEach(card => {
      const productId = card.dataset.productId;
      // Check if it's a bundle product
      const product = this.boxData.selectableProductVariants.find(p => p.productPlatformId === productId);
      
      // Check if it's an upsell product
      const upsellProduct = this.upsellProducts.find(p => p.id === productId);
      
      const originalPriceEl = card.querySelector('.original-price');
      const discountedPriceEl = card.querySelector('.discounted-price');
      
      if (upsellProduct) {
        // Upsell items are always one-time purchase, no subscription discount
        originalPriceEl.style.display = 'none';
        discountedPriceEl.textContent = this.formatPrice(upsellProduct.price);
        discountedPriceEl.style.color = 'rgb(0, 0, 0)'; // Regular price color
      } else if (product) {
        const originalPrice = product.productVariants[0].price;
        const discountedPrice = this.getDiscountedPrice(originalPrice, productId);
        
        // Handle display logic based on plan type and discount
        if (this.selectedPlan === 'onetime' || originalPrice === discountedPrice) {
          // For one-time purchases or no discount, show only the regular price
          originalPriceEl.style.display = 'none';
          discountedPriceEl.textContent = this.formatPrice(originalPrice);
          discountedPriceEl.style.color = 'rgb(0, 0, 0)'; // Regular price color
        } else {
          // For subscriptions with discount, show both prices
          originalPriceEl.style.display = 'block';
          originalPriceEl.innerHTML = `<s>${this.formatPrice(originalPrice)}</s>`;
          discountedPriceEl.textContent = this.formatPrice(discountedPrice);
          discountedPriceEl.style.color = 'rgb(234, 111, 97)'; // Discount price color
        }
      }
    });
  }

  getTotalItems() {
    return Array.from(this.cart.values()).reduce((sum, qty) => sum + qty, 0);
  }

  calculateSubtotal() {
    let total = 0;
    this.cart.forEach((quantity, productId) => {
      // First try regular products
      let product = this.boxData.selectableProductVariants.find(p => p.productPlatformId === productId);
      
      // If not found, check upsell products
      if (!product) {
        const upsellProduct = this.upsellProducts.find(p => p.id === productId);
        if (upsellProduct) {
          let price = upsellProduct.price;
          if (price >= 100) {
            price = price / 100;
          }
          total += price * quantity;
          return;
        }
      }

      // Handle regular product
      if (product) {
        let price = product.productVariants[0].price;
        if (price >= 100) {
          price = price / 100;
        }
        total += price * quantity;
      }
    });
    return total;
  }

  calculateDiscount(subtotal) {
    if (this.selectedPlan === 'onetime') {
      // For one-time purchases, volume discounts apply when thresholds are met
      const tiers = this.boxData.percentDiscountTier || {};
      
      console.log('Volume discount tiers:', tiers);
      console.log('Current subtotal:', subtotal);
      
      if (Object.keys(tiers).length === 0) {
        return 0; // No tiers configured
      }
      
      // Find the highest tier threshold that the subtotal has reached
      // Thresholds are in dollars (e.g., "30" = $30)
      const applicableTier = Object.entries(tiers)
        .map(([threshold, discount]) => [parseFloat(threshold), parseFloat(discount)])
        .sort(([a], [b]) => b - a) // Sort by threshold descending
        .find(([threshold]) => {
          console.log(`Checking tier: subtotal $${subtotal} >= threshold $${threshold}?`, subtotal >= threshold);
          return subtotal >= threshold;
        });
      
      console.log('Applicable tier:', applicableTier);
      
      // Apply the discount if a tier is met
      const discount = applicableTier ? (subtotal * applicableTier[1] / 100) : 0;
      console.log('Calculated discount:', discount);
      
      return discount;
    } else {
      // For subscriptions, discount is already applied to individual items
      // Calculate total discount as sum of individual discounts
      let totalDiscount = 0;
      
      for (const [productId, quantity] of this.cart.entries()) {
        const product = this.boxData.selectableProductVariants.find(p => p.productPlatformId === productId);
        if (product) {
          let originalPrice = product.productVariants[0].price;
          let discountedPrice = this.getDiscountedPrice(originalPrice, productId);
          
          // Convert to dollars if needed
          if (originalPrice >= 100) originalPrice = originalPrice / 100;
          if (discountedPrice >= 100) discountedPrice = discountedPrice / 100;
          
          totalDiscount += (originalPrice - discountedPrice) * quantity;
        }
      }
      
      return totalDiscount;
    }
  }

  getDiscountedPrice(originalPrice, productId = null) {
    if (!this.selectedPlan) {
      return originalPrice;
    }

    if (this.selectedPlan === 'onetime') {
      // For one-time purchases, no individual product discount
      // Tier discounts apply to cart total, not individual items
      return originalPrice;
    } else {
      // For subscriptions, check variantMapping first for specific adjusted prices
      const plan = this.boxData.sellingPlanGroup[this.selectedPlan];
      if (plan && plan.variantMapping && productId) {
        const product = this.boxData.selectableProductVariants.find(p => p.productPlatformId === productId);
        if (product && product.productVariants && product.productVariants[0]) {
          const variantId = product.productVariants[0].platformId;
          const variantMapping = plan.variantMapping[variantId];
          if (variantMapping && typeof variantMapping.adjustedPrice === 'number') {
            return variantMapping.adjustedPrice; // Use the specific adjusted price from API
          }
        }
      }
      
      // Fallback to percentage discount if no specific adjusted price
      if (plan && plan.priceAdjustmentAmount) {
        return originalPrice * (1 - plan.priceAdjustmentAmount / 100);
      }
      
      return originalPrice;
    }
  }

  async handleAddToCart() {
    try {
      // First clear the cart
      await fetch('/cart/clear.js', { method: 'POST' });

      // Prepare all items for cart
      const items = [];

      // Process all cart items
      for (const [productId, quantity] of this.cart.entries()) {
        // Check if it's an upsell product
        const upsellProduct = this.upsellProducts.find(p => p.id === productId);
        if (upsellProduct) {
          // Add upsell as simple product (no subscription)
          items.push({
            id: Number(upsellProduct.variantId),
            quantity: parseInt(quantity)
          });
          continue;
        }

        // Handle bundle item
        const product = this.boxData.selectableProductVariants.find(p => p.productPlatformId === productId);
        const variant = product?.productVariants?.[0];
        if (!variant) continue;

        // Extract numeric variant ID
        let variantId = variant.variantId;
        if (!variantId && variant.platformId?.includes('ProductVariant')) {
          variantId = variant.platformId.match(/(\d+)$/)?.[1];
        }
        if (!variantId) continue;

        // Create bundle item
        const item = {
          id: Number(variantId),
          quantity: parseInt(quantity),
          properties: {
            _dynamicBoxIndex: 0,
            _dynamicBoxId: String(this.boxData.boxId)
          }
        };

        // Add selling plan for subscription bundle items
        if (this.selectedPlan !== 'onetime') {
          const plan = this.boxData.sellingPlanGroup[this.selectedPlan];
          if (plan?.platformId) {
            const sellingPlanId = plan.platformId.match(/(\d+)$/)?.[1];
            if (sellingPlanId) {
              item.selling_plan = Number(sellingPlanId);
            }
          }
        }

        items.push(item);
      }

      if (items.length === 0) {
        throw new Error('No items to add to cart');
      }

      // Add all items to cart at once
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add items to cart');
      }

      // ✅ Immediately redirect to checkout
      window.location.href = '/checkout';
      
    } catch (error) {
      console.error('Add to cart failed:', error);
      this.showError(error.message || 'Failed to add items to cart. Please try again.');
    }
  }

  getSellingPlanId() {
    if (this.selectedPlan === 'onetime') {
      return null;
    }

    const plan = this.boxData.sellingPlanGroup[this.selectedPlan];
    if (plan && plan.variantMapping) {
      const firstVariantMapping = Object.values(plan.variantMapping)[0];
      return firstVariantMapping?.planId || null;
    }
    
    return null;
  }

  formatPrice(amount) {
    const currency = this.boxData.currencyCode || 'USD';
    
    // Convert amount to number if it's a string
    let value = typeof amount === 'number' ? amount : parseFloat(amount);
    
    // Check if the value seems to be in cents (typical for Shopify/Skio APIs)
    // If the value is a large number (> 100), it's likely in cents
    if (value >= 100) {
      value = value / 100; // Convert cents to dollars
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  applyTheme() {
    const theme = this.boxData.theme || {};
    const style = document.createElement('style');
    
    style.textContent = `
      .bundle-header h1 { color: ${theme.header || '#006B5F'}; }
      .bundle-header h3 { color: ${theme.subHeader || '#006B5F'}; }
      .subscription-btn.active {
        background: ${theme.primaryButtonBgSelected || '#000'};
        color: ${theme.primaryButtonTextSelected || '#fff'};
        border-color: ${theme.primaryButtonBorderSelected || '#000'};
      }
      .progress-fill { background: ${theme.progessBar || '#000'}; }
      .add-to-cart-btn {
        background: ${theme.cartButtonBg || '#000'};
        color: ${theme.cartButtonText || '#fff'};
      }
      .products-grid {
        display: grid !important;
        grid-template-columns: repeat(auto-fill, minmax(216px, 1fr)) !important;
        gap: 64px !important;
      }
      @media screen and (min-width: 1024px) {
        .products-grid {
          gap: 80px !important;
        }
      }
      @media screen and (min-width: 1440px) {
        .products-grid {
          gap: 96px !important;
        }
      }
    `;
    
    document.head.appendChild(style);
  }

  handleInfoButtonClick(infoBtn) {
    console.log('Info button clicked!', infoBtn);
    
    // Get the product card
    const productCard = infoBtn.closest('.product-card');
    if (!productCard) {
      console.log('No product card found');
      return;
    }

    const productId = productCard.dataset.productId;
    console.log('Product ID:', productId);
    
    // First try to find in regular products
    let product = this.boxData.selectableProductVariants.find(p => p.productPlatformId === productId);
    
    // If not found, check in upsell products
    if (!product) {
      const upsellProduct = this.upsellProducts.find(p => p.id === productId);
      if (!upsellProduct) {
        console.log('No product found for ID:', productId);
        return;
      }
      
      // For upsell products, fetch full details first
      fetch(`/products/${upsellProduct.handle}.json`)
        .then(response => response.json())
        .then(data => {
          product = {
            productPlatformId: upsellProduct.id,
            productTitle: upsellProduct.title,
            productImageSrc: upsellProduct.featured_image,
            productDescription: data.product.description || data.product.body_html,
            productBody: data.product.body_html,
            productVariants: [{
              platformId: upsellProduct.variantId,
              price: upsellProduct.price
            }]
          };
          this.openProductInfoSidebar(product);
        })
        .catch(error => {
          console.error('Failed to fetch product details:', error);
          // Fallback to basic product info
          product = {
            productPlatformId: upsellProduct.id,
            productTitle: upsellProduct.title,
            productImageSrc: upsellProduct.featured_image,
            productDescription: `Learn more about ${upsellProduct.title}`,
            productVariants: [{
              platformId: upsellProduct.variantId,
              price: upsellProduct.price
            }]
          };
          this.openProductInfoSidebar(product);
        });
      return;
    }

    console.log('Opening sidebar for product:', product);
    
    // Open sidebar with product info
    this.openProductInfoSidebar(product);
  }

  openProductInfoSidebar(product) {
    console.log('Opening sidebar for:', product.productTitle);
    
    const sidebar = document.getElementById('product-info-sidebar');
    const productImg = document.getElementById('sidebar-product-img');
    const productName = document.getElementById('sidebar-product-name');
    const productDescription = document.getElementById('sidebar-product-description');

    if (!sidebar) {
      console.error('Sidebar element not found!');
      return;
    }

    // Set product information
    productImg.src = product.productImageSrc;
    productImg.alt = product.productTitle;
    productName.textContent = product.productTitle;
    
    // Get product description from various possible sources
    let description = '';
    if (product.productDescription && product.productDescription.trim()) {
      description = product.productDescription;
    } else if (product.description && product.description.trim()) {
      description = product.description;
    } else if (product.productBody && product.productBody.trim()) {
      description = product.productBody;
    } else {
      // Fallback description
      description = `Learn more about ${product.productTitle}. This premium product is carefully crafted to deliver exceptional quality and results.`;
    }
    
    console.log('Setting description:', description);
    productDescription.innerHTML = `<p>${description}</p>`;

    // Show sidebar
    console.log('Adding active class to sidebar');
    sidebar.classList.add('active');
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  async handleShowUpsells() {
    this.showingUpsells = true;
    
    // Scroll to top of the page smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Update button text
    const addToCartBtn = document.getElementById('add-to-cart');
    const buttonDiv = addToCartBtn.querySelector('div');
    if (buttonDiv) {
      buttonDiv.textContent = 'Checkout';
    } else {
      addToCartBtn.textContent = 'Checkout';
    }

    // Hide bundle header, progress section, and subscription options
    const bundleHeader = document.querySelector('.bundle-header');
    const progressSection = document.querySelector('.progress-section');
    const subscriptionOptions = document.getElementById('subscription-options');
    
    if (bundleHeader) bundleHeader.style.display = 'none';
    if (progressSection) progressSection.style.display = 'none';
    subscriptionOptions.style.display = 'none';

    // Update product groups with upsell products
    const container = document.getElementById('product-groups');
    container.innerHTML = '';
    
    // Create wrapper div for header section
    const headerWrapper = document.createElement('div');
    headerWrapper.style.display = 'flex';
    headerWrapper.style.flexDirection = 'column';
    headerWrapper.style.alignItems = 'center';
    headerWrapper.style.justifyContent = 'center';
    headerWrapper.style.textAlign = 'center';
    headerWrapper.style.marginBottom = '32px';
    headerWrapper.style.gap = '24px';
    headerWrapper.style.position = 'relative';
    headerWrapper.style.paddingTop = '40px';

    // Add back link with new styling
    const backLink = document.createElement('div');
    backLink.style.display = 'flex';
    backLink.style.alignItems = 'center';
    backLink.style.gap = '8px';
    backLink.style.position = 'absolute';
    backLink.style.cursor = 'pointer';
    backLink.style.top = '0';
    backLink.style.left = '0';
    backLink.addEventListener('click', () => this.handleShowMainProducts());

    // Create icon container
    const iconContainer = document.createElement('div');
    iconContainer.style.display = 'flex';
    iconContainer.style.alignItems = 'center';
    iconContainer.style.justifyContent = 'center';
    
    // Add SVG icon
    iconContainer.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" 
           aria-hidden="true" data-slot="icon" style="width: 18px; height: 18px; color: #000000;">
        <path fill-rule="evenodd" 
              d="M7.72 12.53a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 1 1 1.06 1.06L9.31 12l6.97 6.97a.75.75 0 1 1-1.06 1.06l-7.5-7.5Z" 
              clip-rule="evenodd">
        </path>
      </svg>`;
    
    // Add text
    const textDiv = document.createElement('div');
    textDiv.tabIndex = 0;
    textDiv.style.color = 'rgb(0, 0, 0)';
    textDiv.style.fontSize = '16px';
    textDiv.style.fontWeight = '500';
    textDiv.style.lineHeight = '1';
    textDiv.textContent = 'Modify box';

    // Assemble back link
    backLink.appendChild(iconContainer);
    backLink.appendChild(textDiv);

    // Add heading
    const heading = document.createElement('h1');
    heading.textContent = 'Want any add-ons?';
    heading.style.color = 'rgb(0, 107, 95)';
    heading.style.fontSize = '36px';
    heading.style.fontWeight = '700';
    heading.style.margin = '0';
    heading.style.lineHeight = '1.2';

    // Add elements to wrapper
    headerWrapper.appendChild(backLink);
    headerWrapper.appendChild(heading);
    container.appendChild(headerWrapper);
    
    const productsGrid = document.createElement('div');
    productsGrid.className = 'products-grid';


    // Display fetched upsell products
    console.log('Displaying upsell products:', this.upsellProducts);
    
    for (const product of this.upsellProducts) {
      try {
        const card = this.createProductCard({
          productPlatformId: product.id,
          productTitle: product.title,
          productImageSrc: product.featured_image,
          productVariants: [{
            platformId: product.variantId,
            price: product.price
          }]
        });
        
        productsGrid.appendChild(card);
      } catch (error) {
        console.error(`Failed to create product card for ${product.id}:`, error);
      }
    }

    container.appendChild(productsGrid);    
    this.updatePricing();
  }

  handleShowMainProducts() {
    this.showingUpsells = false;
    
    // Scroll to top of the page smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Update button text
    const addToCartBtn = document.getElementById('add-to-cart');
    const buttonDiv = addToCartBtn.querySelector('div');
    if (buttonDiv) {
      buttonDiv.textContent = 'Next';
    } else {
      addToCartBtn.textContent = 'Next';
    }
    
    // Clear the product groups container first
    const container = document.getElementById('product-groups');
    container.innerHTML = '';

    // Show bundle header, progress section, and subscription options
    const bundleHeader = document.querySelector('.bundle-header');
    const progressSection = document.querySelector('.progress-section');
    const subscriptionOptions = document.getElementById('subscription-options');
    
    if (bundleHeader) bundleHeader.style.display = 'block';
    if (progressSection) progressSection.style.display = 'block';
    subscriptionOptions.style.display = 'flex';

    // Restore original product groups
    this.renderProductGroups();
  }

  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-toast';
    errorDiv.textContent = message;
    document.body.appendChild(errorDiv);
    
    setTimeout(() => {
      errorDiv.remove();
    }, 3000);
  }
}

// Global functions for sidebar control
window.closeProductInfo = function() {
  const sidebar = document.getElementById('product-info-sidebar');
  sidebar.classList.remove('active');
  
  // Restore body scroll
  document.body.style.overflow = '';
}

// Close sidebar on escape key
document.addEventListener('keydown', function(e) {
  if (e.key === 'Escape') {
    window.closeProductInfo();
  }
});

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new BuildYourBundle();
});
